import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { useCyclingToasts } from '../../hooks/useCyclingToasts';
import type { Route } from '../../types/storage';

interface RenameRouteModalProps {
  open: boolean;
  route: Route | null;
  onClose: () => void;
  onRename: (routeId: string, newName: string) => Promise<void>;
}

export const RenameRouteModal: React.FC<RenameRouteModalProps> = ({
  open,
  route,
  onClose,
  onRename
}) => {
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSelectedText, setHasSelectedText] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cyclingToasts = useCyclingToasts();

  useEffect(() => {
    if (route) {
      setNewName(route.name);
    }
    setError(null);
    // Reset the text selection flag when a new route is loaded
    setHasSelectedText(false);
  }, [route]);

  // Effect to select text when modal opens and name is set (only once)
  useEffect(() => {
    if (open && newName && !hasSelectedText) {
      // Function to try selecting text
      const trySelect = () => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
          setHasSelectedText(true); // Mark that we've done the selection
          return true;
        }
        return false;
      };
      
      // Try immediately
      if (trySelect()) {
        return;
      }
      
      // If ref not available yet, keep trying with increasing delays
      const attempts = [50, 100, 200, 300, 500];
      const timers: number[] = [];
      
      attempts.forEach((delay) => {
        const timer = setTimeout(() => {
          if (trySelect()) {
            // Success, clear remaining timers
            timers.forEach(clearTimeout);
          }
        }, delay);
        timers.push(timer as any);
      });
      
      return () => {
        timers.forEach(clearTimeout);
      };
    }
  }, [open, newName, hasSelectedText]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!route?.id) {
      setError('Invalid route selected');
      return;
    }

    if (!newName.trim()) {
      setError('Route name cannot be empty');
      return;
    }

    if (newName.trim() === route.name) {
      // No change, just close
      onClose();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onRename(route.id, newName.trim());
      cyclingToasts.routeUpdated(newName.trim());
      onClose();
    } catch (err) {
      console.error('Error renaming route:', err);
      setError('Failed to rename route. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setNewName('');
      setError(null);
      setHasSelectedText(false); // Reset selection flag
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit
      }}
      disableRestoreFocus={true}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" fontSize="small" />
            Rename Route
          </Typography>
          <IconButton 
            onClick={handleClose}
            size="small"
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            label="Enter route name"
            variant="outlined"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            error={!!error}
            helperText={error}
            disabled={loading}
            size="small"
            inputProps={{
              maxLength: 100
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          variant="contained"
          disabled={loading || !newName.trim() || newName.trim() === route?.name}
          startIcon={<EditIcon />}
        >
          {loading ? 'Renaming...' : 'Rename'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
